import React from "react";
import PropTypes from "prop-types";
import { formatMoney, formatRelativeTime } from "../../Budgets/formatters";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

/**
 * impactAmount is the signed change (delta): proposed - previous.
 * previousAmount is the current/previous budget amount (optional).
 */
function computeImpact(impactAmount, previousAmount, currency) {
  const hasDelta = impactAmount != null;
  const hasPrevious = previousAmount != null && previousAmount !== 0;

  let delta = impactAmount;
  let percentChange = null;
  let direction = "";

  if (hasDelta) {
    direction = impactAmount >= 0 ? "Increase" : "Decrease";
    if (hasPrevious) {
      percentChange = (impactAmount / previousAmount) * 100;
    }
  }

  let prefix = "";
  if (delta != null && delta >= 0) prefix = "+";
  const formattedDelta =
    delta == null
      ? null
      : formatMoney(Math.round(Math.abs(delta) * 100), currency);

  let formattedPercent = null;
  if (percentChange != null) {
    const sign = percentChange >= 0 ? "+" : "";
    formattedPercent = `${sign}${percentChange.toFixed(1)}%`;
  }

  const increaseOrDecrease =
    formattedDelta == null
      ? null
      : `${prefix}${formattedDelta} budget ${direction.toLowerCase()}`;

  return {
    formattedDelta,
    formattedPercent,
    increaseOrDecrease,
  };
}

const DecisionSummary = ({
  budgetName,
  periodLabel,
  impactAmount,
  impactCurrency,
  previousAmount,
  requesterName,
  requesterTitle,
  requestedAt,
}) => {
  const currency = impactCurrency || "EUR";
  const { formattedPercent, increaseOrDecrease } =
    computeImpact(impactAmount, previousAmount, currency);


  const budgetPeriodLine =
    budgetName && periodLabel
      ? `${budgetName} - ${periodLabel}`
      : budgetName || periodLabel || null;

  let requesterLine = null;
  if (requesterName && requesterTitle) {
    requesterLine = `Requested by ${requesterName} - ${requesterTitle}`;
  } else if (requesterName) {
    requesterLine = `Requested by ${requesterName}`;
  }

  const hasPrevious = typeof previousAmount === "number";
  const hasDelta = typeof impactAmount === "number";

  let formattedCurrentBudget = null;
  if (hasPrevious && previousAmount !== null) {
    formattedCurrentBudget = formatMoney(
      Math.round(previousAmount * 100),
      currency
    );
  }

  const requestedAmount =
    hasPrevious && hasDelta ? previousAmount + impactAmount : null;

  let formattedRequestedBudget = null;
  if (requestedAmount !== null) {
    formattedRequestedBudget = formatMoney(
      Math.round(requestedAmount * 100),
      currency
    );
  }

  return (
    <div className="bg-card border border-card-bd rounded-card p-5 mb-6 text-text">
      <div className="flex items-center gap-2 mb-4">
        <FontAwesomeIcon
          icon={icon({ name: "list", family: "sharp", style: "light" })}
          className="text-text-2 text-sm"
        />
        <h3 className="text-sm font-semibold text-text">
          Decision Summary
        </h3>
      </div>

      <div className="space-y-4 text-sm text-text">
        <div>
          <p className="text-lg font-bold">{increaseOrDecrease}</p>
          {formattedPercent != null && (
            <p className="text-base font-medium">
              {formattedPercent} vs current budget
            </p>
          )}
        </div>

        <div>
          <p className="text-base font-medium">{budgetPeriodLine}</p>
        </div>

        <div>
          <p>
            Current budget:{" "}
            {formattedCurrentBudget === null ? "—" : formattedCurrentBudget}
          </p>
          <p>
            Requested budget:{" "}
            {formattedRequestedBudget === null ? "—" : formattedRequestedBudget}
          </p>
        </div>

        {requesterLine && (
          <div>
            <p className="text-sm text-text">{requesterLine}</p>
          </div>
        )}

        {requestedAt && (
          <p className="text-xs text-text-2">
            Requested {formatRelativeTime(requestedAt)}
          </p>
        )}
      </div>
    </div>
  );
};

DecisionSummary.propTypes = {
  budgetName: PropTypes.string,
  periodLabel: PropTypes.string,
  impactAmount: PropTypes.number,
  impactCurrency: PropTypes.string,
  previousAmount: PropTypes.number,
  requesterName: PropTypes.string,
  requesterTitle: PropTypes.string,
  requestedAt: PropTypes.string,
};

export default DecisionSummary;
